import React, { useCallback } from 'react';
import { Gender, GuestDetail } from '@camp67/model';
import clsx from 'clsx';

type OnGuestChange = <T extends keyof GuestDetail>(
  id: string,
  key: T,
  value: GuestDetail[T],
) => void;

interface GuestListProps {
  totalTentCapacity: number;
  guestCount: number;
  guests: GuestDetail[];
  primaryContactId: string;
  onGuestChange: (id: string, details: Partial<GuestDetail>) => void;
  messages: {
    tooManyGuests: (totalTentCapacity: number, guestCount: number) => React.ReactNode;
    additionalBed: string;
    enterName: string;
    enterAge: string;
    selectGender: string;
    genderMale: string;
    genderFemale: string;
    genderNonBinary: string;
    genderPreferNotToSay: string;
  };
}

export const GuestList: React.FC<GuestListProps> = ({
  totalTentCapacity,
  guestCount,
  guests,
  primaryContactId,
  onGuestChange,
  messages,
}) => {
  const totalAdditionalBedCards = Math.max(0, totalTentCapacity - guestCount);

  const onChange: OnGuestChange = useCallback(
    (id, key, value) => {
      onGuestChange(id, { [key]: value });
    },
    [onGuestChange],
  );

  const isGuestCountExceedingRoomCapacity = guestCount > totalTentCapacity;

  if (isGuestCountExceedingRoomCapacity) {
    return (
      <div className="border-red-200 bg-red-50 border-2 rounded-md mt-2 py-3 px-3">
        <p className="text-red-400 text-lg font-medium">
          {messages.tooManyGuests(totalTentCapacity, guestCount)}
        </p>
      </div>
    );
  }

  return (
    <div>
      {guests.map((guest) => (
        <GuestCard
          {...guest}
          key={guest.id}
          onChange={onChange}
          isPrimaryContact={guest.id === primaryContactId}
          messages={messages}
        />
      ))}
      {totalAdditionalBedCards > 0
        ? Array.from(Array(totalAdditionalBedCards)).map((_, index) => (
            <div
              key={index}
              className="w-full py-3 px-5 bg-gray-100 rounded-md text-sm mt-3 uppercase tracking-widest text-gray-400"
            >
              {messages.additionalBed}
            </div>
          ))
        : null}
    </div>
  );
};

type GuestCardProps = GuestDetail & {
  onChange: OnGuestChange;
  isPrimaryContact: boolean;
  messages: GuestListProps['messages'];
};

const genders: Gender[] = ['male', 'female', 'non-binary', 'prefer-not-to-say'];

const GuestCard: React.FC<GuestCardProps> = ({
  id,
  name,
  age,
  gender,
  onChange,
  isPrimaryContact,
  messages,
}) => {
  const genderLabels: Record<Gender, string> = {
    male: messages.genderMale,
    female: messages.genderFemale,
    none: messages.selectGender,
    'non-binary': messages.genderNonBinary,
    'prefer-not-to-say': messages.genderPreferNotToSay,
  };

  return (
    <div
      className={clsx(
        'w-full py-2 md:py-0 flex flex-col md:flex-row items-center gap-2 md:gap-4 rounded-md text-base mt-3',
        {
          'md:bg-gray-100': !isPrimaryContact,
          'md:bg-orange-100': isPrimaryContact,
        },
      )}
    >
      <input
        onChange={(e) => onChange(id, 'name', e.target.value)}
        value={name}
        type="text"
        className={clsx(
          'py-3 px-5 rounded-md w-full md:w-1/2 md:h-full md:py-3 placeholder:text-gray-400 outline-none border-none',
          {
            'bg-gray-100 md:bg-transparent': !isPrimaryContact,
            'bg-orange-100 md:bg-transparent': isPrimaryContact,
          },
        )}
        placeholder={messages.enterName}
      />
      <input
        type="number"
        className={clsx(
          'md:ml-0 px-5 rounded-md md:rounded-none py-3 w-full md:w-1/4 md:h-full md:py-3 md:px-2 outline-none md:focus-within:bg-blue-100',
          {
            'bg-gray-100 md:bg-transparent': !isPrimaryContact,
            'bg-orange-100 md:bg-transparent': isPrimaryContact,
          },
        )}
        placeholder={messages.enterAge}
        required={true}
        onChange={(e) => onChange(id, 'age', parseInt(e.target.value))}
        onWheelCapture={(e) => {
          e.currentTarget.blur();
        }}
        value={age !== 0 ? age : ''}
        min={1}
        max={120}
      />
      <div
        className={clsx(
          'px-5 py-3 md:py-3 md:px-2 w-full md:w-1/4 md:h-full rounded-md md:rounded-none md:rounded-r-md md:focus-within:bg-blue-100',
          {
            'bg-gray-100 md:bg-transparent': !isPrimaryContact,
            'bg-orange-100 md:bg-transparent': isPrimaryContact,
          },
        )}
      >
        <select
          value={gender}
          onChange={(e) => onChange(id, 'gender', e.target.value as Gender)}
          className={clsx('-ml-1.5 md:ml-0 w-full bg-transparent outline-none', {
            'text-gray-400': gender === 'none',
          })}
        >
          <option value={'none'} label={messages.selectGender} disabled>
            {messages.selectGender}
          </option>
          {genders.map((g, key) => (
            <option key={key} value={g} label={genderLabels[g]}>
              {genderLabels[g]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
